import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, inject, Output } from '@angular/core';
import { ChatMember } from '../../models/chat.models';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import { ChatStore } from '../../services/chat.store';
import { dayLabel } from '../../utils/chat-date.util';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';

/**
 * The right-hand Group Info panel: who's in the group, who owns it, and the owner's actions.
 *
 * Permission handling is by omission — a member simply doesn't see Edit / Add / Delete, so the
 * panel never offers something the server would reject. Leaving is available to everyone,
 * including the owner (ownership then transfers, per the backend spec §4.1).
 */
@Component({
  selector: 'app-chat-group-info',
  standalone: true,
  imports: [CommonModule, ChatAvatarComponent],
  templateUrl: './chat-group-info.component.html',
  styleUrls: ['./chat-group-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatGroupInfoComponent {
  readonly store = inject(ChatStore);
  private readonly remmiConfirm = inject(RemmiConfirmService);

  @Output() closed = new EventEmitter<void>();
  @Output() manage = new EventEmitter<void>();
  @Output() addMembers = new EventEmitter<void>();

  readonly dayLabel = dayLabel;

  get detail() {
    return this.store.activeDetail();
  }

  isMe(member: ChatMember): boolean {
    return member.user_id === this.store.myId;
  }

  removeMember(member: ChatMember): void {
    const detail = this.detail;
    if (!detail) {
      return;
    }
    this.remmiConfirm
      .delete({
        title: 'Remove Member?',
        message: `They will be removed from "${detail.name}".`,
        subject: member.name,
        confirmLabel: 'Remove',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.removeMember(detail.conversation_id, member.user_id);
        }
      });
  }

  copyGroup(): void {
    const detail = this.detail;
    if (detail) {
      this.store.copyGroup(detail.conversation_id);
    }
  }

  leaveGroup(): void {
    const detail = this.detail;
    if (!detail) {
      return;
    }
    this.remmiConfirm
      .ask({
        title: 'Leave Group?',
        message: "You'll stop receiving its messages.",
        subject: detail.name,
        confirmLabel: 'Leave',
        tone: 'warning',
        icon: 'pi pi-sign-out',
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.leaveGroup(detail.conversation_id);
        }
      });
  }

  deleteGroup(): void {
    const detail = this.detail;
    if (!detail) {
      return;
    }
    this.remmiConfirm
      .delete({
        title: 'Delete Group?',
        message: 'This removes the group for everyone.',
        subject: detail.name,
      })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.deleteGroup(detail.conversation_id);
        }
      });
  }

  /** A deactivated colleague keeps their membership and their messages, but is not reachable. */
  isInactive(member: ChatMember): boolean {
    return member.is_active === false;
  }

  statusLabel(member: ChatMember): string {
    if (this.isInactive(member)) {
      return 'Deactivated';
    }
    return member.status === 'online' ? 'Online' : member.status === 'dnd' ? 'Do Not Disturb' : 'Offline';
  }

  trackByMember = (_: number, member: ChatMember) => member.user_id;
}
